import React from 'react';
import ImageUploadBox from '@/components/common/ImageUploadBox';
import { Select } from '@/components/common/Select';

export interface CopyrightFormProps {
  data: Api.Application.Step4Request;
  onChange: (data: Api.Application.Step4Request) => void;
}

const COPYRIGHT_CATEGORY_OPTIONS = [
  { label: '核心版权证明', value: 'COPYRIGHT_CORE' },
  { label: '辅助版权/改编授权', value: 'COPYRIGHT_AUXILIARY' },
];

export default function CopyrightForm({ data, onChange }: CopyrightFormProps) {
  const update = <K extends keyof Api.Application.Step4Request>(
    field: K,
    value: Api.Application.Step4Request[K]
  ) => {
    onChange({ ...data, [field]: value });
  };

  const attachments = data.attachments && data.attachments.length > 0
    ? data.attachments
    : [
      {
        category: 'COPYRIGHT_CORE',
        display_name: '核心版权证明文件',
        upload_asset_id: '',
        visibility: 'FRONTEND_VISIBLE',
      },
    ];

  const updateAttachment = (index: number, fields: Partial<Api.Application.Step4AttachmentRequest>) => {
    const nextList = [...attachments];
    nextList[index] = { ...nextList[index], ...fields };
    update('attachments', nextList);
  };

  const addAttachment = () => {
    const nextCategory = data.is_adapted_work ? 'COPYRIGHT_AUXILIARY' : 'COPYRIGHT_CORE';
    const nextDisplayName = nextCategory === 'COPYRIGHT_AUXILIARY' ? '改编授权证明文件' : '核心版权证明文件';
    update('attachments', [
      ...attachments,
      {
        category: nextCategory,
        display_name: nextDisplayName,
        upload_asset_id: '',
        visibility: 'FRONTEND_VISIBLE',
      },
    ]);
  };

  const removeAttachment = (index: number) => {
    if (attachments.length <= 1) {
      update('attachments', [
        {
          category: 'COPYRIGHT_CORE',
          display_name: '',
          upload_asset_id: '',
          visibility: 'FRONTEND_VISIBLE',
        },
      ]);
      return;
    }
    const nextList = attachments.filter((_, i) => i !== index);
    update('attachments', nextList);
  };

  return (
    <div className="space-y-6">
      <p className="text-sm text-foreground-600 mb-4">提交版权权属声明与证明文件，确保内容合规合法。</p>

      {/* 版权属性声明 (独立绑定 is_original_right_holder 与 is_adapted_work) */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <label className={`flex items-center gap-3.5 p-4 sm:p-5 rounded-xl border cursor-pointer transition-all ${(data.is_original_right_holder ?? true)
            ? 'border-primary-500 bg-primary-50/40 ring-1 ring-primary-500/20'
            : 'border-gray-200 hover:bg-gray-50'
          }`}>
          <input
            type="checkbox"
            checked={data.is_original_right_holder ?? true}
            onChange={(e) => update('is_original_right_holder', e.target.checked)}
            className="w-4.5 h-4.5 text-primary-500 rounded border-gray-300 focus:ring-primary-400 cursor-pointer"
          />
          <div>
            <span className="text-base font-bold text-foreground-900">原始权利人</span>
            <p className="text-xs md:text-sm text-foreground-400 mt-0.5">是否为著作权原始所有人</p>
          </div>
        </label>

        <label className={`flex items-center gap-3.5 p-4 sm:p-5 rounded-xl border cursor-pointer transition-all ${Boolean(data.is_adapted_work)
            ? 'border-primary-500 bg-primary-50/40 ring-1 ring-primary-500/20'
            : 'border-gray-200 hover:bg-gray-50'
          }`}>
          <input
            type="checkbox"
            checked={Boolean(data.is_adapted_work)}
            onChange={(e) => update('is_adapted_work', e.target.checked)}
            className="w-4.5 h-4.5 text-primary-500 rounded border-gray-300 focus:ring-primary-400 cursor-pointer"
          />
          <div>
            <span className="text-base font-bold text-foreground-900">改编作品</span>
            <p className="text-xs md:text-sm text-foreground-400 mt-0.5">是否基于原著网络小说/IP改编</p>
          </div>
        </label>
      </div>

      {/* 权利方名称 */}
      <div>
        <label className="block text-sm font-bold text-foreground-800 mb-2">
          权利方名称 <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          value={data.material_right_holder_name || ''}
          onChange={(e) => update('material_right_holder_name', e.target.value)}
          placeholder="与认证主体一致或填写授权方全称"
          className="w-full px-4 py-3 rounded-lg bg-white border border-gray-200/80 text-base text-foreground-950 placeholder-foreground-400 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-400/20 transition-all"
        />
      </div>

      {/* 素材/原著作品名称 */}
      <div>
        <label className="block text-sm font-bold text-foreground-800 mb-2">
          原著/素材作品名称 <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          value={data.material_work_title || ''}
          onChange={(e) => update('material_work_title', e.target.value)}
          placeholder="请输入素材或改编原著作品名称"
          className="w-full px-4 py-3 rounded-lg bg-white border border-gray-200/80 text-base text-foreground-950 placeholder-foreground-400 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-400/20 transition-all"
        />
      </div>

      {/* 版权证明文件列表 (支持下拉选择 COPYRIGHT_CORE / COPYRIGHT_AUXILIARY 类别) */}
      <div className="space-y-4 pt-2 border-t border-gray-100">
        <div className="flex items-center justify-between">
          <label className="block text-sm font-bold text-foreground-800">
            版权证明文件列表 <span className="text-red-500">*</span>
          </label>
          <button
            type="button"
            onClick={addAttachment}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-primary-600 bg-primary-50 rounded-lg hover:bg-primary-100 transition-colors cursor-pointer"
          >
            <i className="ri-add-line text-sm"></i>
            添加证明文件
          </button>
        </div>

        {attachments.map((item, idx) => {
          const itemUrl = (item as any)?.access_url || (item as any)?.upload_asset?.access_url || '';
          const itemAssetId = item.upload_asset_id || (item as any)?.upload_asset?.id || '';
          const itemValue = itemUrl || itemAssetId;
          const fileName =
            item.display_name ||
            (item as any)?.upload_asset?.display_name ||
            (item as any)?.upload_asset?.file_name ||
            '';

          return (
            <div
              key={idx}
              className="p-5 rounded-xl bg-white border border-gray-200/80 shadow-2xs space-y-4 relative"
            >
              <div className="flex items-center justify-between pb-3 border-b border-gray-100">
                <span className="text-sm font-bold text-foreground-800 flex items-center gap-2">
                  <i className="ri-file-shield-line text-primary-600 text-base"></i>
                  版权证明附件 #{idx + 1}
                </span>
                {attachments.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeAttachment(idx)}
                    className="text-xs text-red-500 hover:text-red-700 font-medium cursor-pointer transition-colors"
                  >
                    <i className="ri-delete-bin-line mr-1"></i>删除
                  </button>
                )}
              </div>

              {/* 分类下拉与名称输入框 */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-end">
                <div>
                  <label className="block text-xs font-bold text-foreground-700 mb-1.5">
                    文件类型 (Category) <span className="text-red-500">*</span>
                  </label>
                  <Select
                    size="md"
                    value={item.category || 'COPYRIGHT_CORE'}
                    onChange={(val) => updateAttachment(idx, { category: String(val) })}
                    options={COPYRIGHT_CATEGORY_OPTIONS}
                    placeholder="请选择文件类型"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-foreground-700 mb-1.5">
                    文件名称/说明
                  </label>
                  <input
                    type="text"
                    value={item.display_name || ''}
                    onChange={(e) => updateAttachment(idx, { display_name: e.target.value })}
                    placeholder="例：著作权登记证书 / 改编授权书"
                    className="w-full h-[42px] px-3.5 rounded-lg bg-white border border-gray-200/80 text-sm text-foreground-950 placeholder-foreground-400 outline-none focus:border-primary-400 transition-all"
                  />
                </div>
              </div>

              {/* 文件上传区域 */}
              <div>
                <label className="block text-xs font-bold text-foreground-700 mb-1.5">
                  上传证明附件 <span className="text-red-500">*</span>
                </label>
                <ImageUploadBox
                  value={itemValue}
                  fileName={fileName}
                  onChange={(assetId) => updateAttachment(idx, { upload_asset_id: assetId })}
                  scene="APPLICATION"
                  policy="uploader-only"
                  accept=".pdf,.jpg,.jpeg,.png"
                  placeholder="点击或拖拽上传证明文件（著作权登记证书 / 授权书 / 合作协议）"
                  subPlaceholder="支持 PDF, JPG, PNG 格式文件"
                />
              </div>
            </div>
          );
        })}
      </div>

      {/* 权属合规确认声明 */}
      <label className="flex items-start gap-3.5 p-4.5 sm:p-5 rounded-xl bg-amber-50/60 border border-amber-200/70 cursor-pointer">
        <input
          type="checkbox"
          checked={data.declaration_confirmed ?? true}
          onChange={(e) => update('declaration_confirmed', e.target.checked)}
          className="mt-1 w-4.5 h-4.5 text-primary-500 rounded border-gray-300 focus:ring-primary-400 cursor-pointer"
        />
        <span className="text-sm text-foreground-800 leading-relaxed">
          我保证所提交作品权属清晰无争议，拥有完整合法授权，且内容不侵犯任何第三方的著作权、商标权及合法权益。<span className="text-red-500 font-bold">*</span>
        </span>
      </label>
    </div>
  );
}